/**
 * Cache tags for server-rendered HTML.
 *
 * The API side already computes tags per GraphQL response
 * (`server/utils/cacheTags.ts`). A server-rendered page is just the union of
 * every response that went into it, so rather than walking the payload a second
 * time the SSR fetch collects the header the API already returned and this
 * module reduces the union to something CloudFront will accept.
 */

export const CACHE_TAG_HEADER = 'x-amz-meta-cache-tag'

/** Where the per-request union lives on the H3 event during SSR. */
export const CACHE_TAG_CONTEXT_KEY = '__cacheTags'

/** Same ceiling as the API side: CloudFront stores 50 and silently drops the rest. */
const CLOUDFRONT_TAG_LIMIT = 50

/**
 * The page chrome whose tag is kept outside the entity budget and through a
 * spill, the way `nav:` is: the announcement bar.
 *
 * The bar is server-rendered into every default-layout page, and switching it
 * off — a wrong promo pulled, the global switch — has to reach every cached
 * copy. As an ordinary entity tag it would not: a page over budget spills to no
 * entity tags at all, and would keep showing the bar for the HTML's hour plus
 * its day of stale-while-revalidate. Reserving the slot costs a page with the
 * bar no room: as an entity the tag would have taken one of the same slots.
 */
export const ANNOUNCEMENT_BAR_OPERATION = 'announcementBar'

/** Must match `ANNOUNCEMENT_BAR_HTML_TAG_PREFIX` in vendure-app's edge-cache-invalidation `tags.ts`. */
export const ANNOUNCEMENT_BAR_HTML_TAG_PREFIX = 'ab:'

/**
 * The bar's tag on HTML: `ab:` + the channel TOKEN, e.g. `ab:cz-channel`.
 *
 * Per channel because the bar is authored per channel: the backend purges only
 * the tags of the channels an edit or a schedule boundary touched, so the other
 * markets' cached pages stay put. Stamped as the bare `op:announcementBar`, each
 * of those would flush the HTML of every locale. The backend still queues that
 * tag on every bar event, but with the HTML no longer carrying it, it reaches
 * only the bar's own API objects — one per locale, cheap to refill.
 *
 * Keyed by the token, not the locale or its language, because the token is what
 * the response already names (`ch:<token>`) and what the backend's event knows
 * the channel by. A language would also be wrong: `/eu` runs on
 * `en-eu-channel`, whose language is `en`.
 */
export function announcementBarTag(channelToken: string): string {
  return `${ANNOUNCEMENT_BAR_HTML_TAG_PREFIX}${channelToken}`
}

/** What `extractCacheTags` names every response's channel by, `ch:<token>`. */
const CHANNEL_TAG_PREFIX = 'ch:'

/**
 * The bar's tag on a page: its `ab:` tag, or the bare operation tag it falls
 * back to (see `htmlTagsForResponse`), never both. A page carries at most one:
 * it renders the bar once, for one channel.
 */
function isChromeTag(tag: string): boolean {
  return tag.startsWith(ANNOUNCEMENT_BAR_HTML_TAG_PREFIX) || tag === `op:${ANNOUNCEMENT_BAR_OPERATION}`
}

/**
 * Reserved for the constant `html` tag, `nav:` and the chrome tag above — at
 * most one of each on a page. Not for `spill`: it only ever replaces the entity
 * tags, never sits beside them, so it reuses their slots. The widest header is
 * therefore exactly CloudFront's 50.
 */
const RESERVED = 3

export const HTML_ENTITY_TAG_BUDGET = CLOUDFRONT_TAG_LIMIT - RESERVED

export const SPILL_TAG = 'spill'

/**
 * Constant tag on every server-rendered document, so a deploy can purge all
 * cached HTML in one call without touching anything else.
 *
 * It has to be constant. The obvious alternative, a `build:<id>` tag purged by
 * the previous id, cannot work: Nuxt regenerates `runtimeConfig.app.buildId` as
 * a random UUID on every build, so CI can neither predict the new id nor recover
 * the old one. A constant tag needs no knowledge of either.
 *
 * It also has to be its own tag rather than a `/*` invalidation, which would
 * additionally wipe `/_nuxt/*`. Those chunks are served from an append-only
 * MinIO bucket at a one-year edge TTL, and that is the only reason HTML from the
 * previous build stays renderable at all after a deploy.
 *
 * Being distinct from `p:`/`c:`/`nav:` cuts both ways and is the point: a
 * catalog purge never touches HTML wholesale, and a deploy purge never touches a
 * cached API response.
 */
export const HTML_TAG = 'html'

/**
 * One tag standing in for the whole navigation, per locale.
 *
 * The reason is budget, not semantics. The nav renders on **every** page and the
 * `collections` response carries a `c:` tag for all 33 of them, so without this a
 * PDP would spend 33 of its 47 slots on the nav before naming its own product, and
 * a collection page listing 24 products would blow the budget outright and spill to
 * no entity tags at all. Collapsing them to one tag costs precision the nav does not
 * need: a collection edit purges every page either way, because the nav is on every
 * page.
 */
export function navTag(locale: string): string {
  return `nav:${locale}`
}

/**
 * The operation whose tags collapse into `navTag`. Only the literal `collections`
 * query, which is the nav. `homepageFeaturedCollections` also returns collections
 * but they are page content, so it keeps its own `c:` tags.
 */
export const NAV_OPERATION = 'collections'

/**
 * Page content whose response collapses to its own `op:` tag on HTML.
 *
 * The same trade as the nav, for the same reason: budget. A collection page
 * server-renders the outfits placed on it, and the `looks` response names every
 * garment on every outfit (`p:`) and each garment's collections (`c:`). On a page
 * that already names two dozen products of its own, that walks the HTML past
 * `HTML_ENTITY_TAG_BUDGET`, and a spill drops EVERY entity tag — the page's own
 * products included — so neither a product edit nor a look edit could purge it.
 *
 * Keeping only `op:looks` still purges the page on the edits that change the
 * cards: any look edit (the backend queues `op:looks` for it), and any collection
 * change through `nav:`. What it gives up: a product edit purges `p:`, which the
 * cards no longer carry, so a garment renamed or unpublished since keeps its
 * marker in the cached page until the HTML's own hour runs out — unless the page
 * lists that product in its grid, which purges it anyway. The cards show no price
 * or stock, so nothing time-sensitive rides on it.
 *
 * `lookCards` is the `/looks` grid's server-rendered slices, collapsed for the
 * same reason: eight outfits' garments and those garments' collections come
 * close to the budget on their own. The backend queues `op:lookCards` on every
 * look edit, the same way it queues `op:looks`.
 */
export const OPERATION_ONLY_HTML_OPERATIONS: ReadonlySet<string> = new Set(['looks', 'lookCards'])

/**
 * The tags one server-side GraphQL response contributes to its page's HTML:
 * `nav:<locale>` for the nav, the bare `op:` tag for the operations above, the
 * channel's `ab:` tag in place of the announcement bar's `op:` tag, and every
 * tag unchanged for anything else. Detected on the operation tag the response
 * already carries, not the request URL, which holds aliases and query params.
 *
 * The bar's `op:` tag is replaced, never kept beside `ab:`: the backend queues it
 * on every bar event, so on the HTML it would purge every locale's pages anyway
 * and the scoping would buy nothing. A response with no `ch:` tag keeps it, as
 * the one fallback that still purges the page: without a channel there is no
 * `ab:` tag to name. In practice that means an empty `Storefront-Token`, which
 * Vendure answers with its default channel, or a token `extractCacheTags` refuses
 * as a tag. The singular `NUXT_STOREFRONT_TOKEN` still names its channel and
 * gets `ab:`.
 */
export function htmlTagsForResponse(tags: readonly string[], localeCode: string): string[] {
  if (tags.includes(`op:${NAV_OPERATION}`))
    return [navTag(localeCode)]
  for (const operation of OPERATION_ONLY_HTML_OPERATIONS) {
    if (tags.includes(`op:${operation}`))
      return [`op:${operation}`]
  }
  const barTag = `op:${ANNOUNCEMENT_BAR_OPERATION}`
  if (tags.includes(barTag)) {
    const channelToken = tags.find(t => t.startsWith(CHANNEL_TAG_PREFIX))?.slice(CHANNEL_TAG_PREFIX.length)
    if (channelToken)
      return tags.map(t => t === barTag ? announcementBarTag(channelToken) : t)
  }
  return [...tags]
}

/**
 * Reduces a collected tag set to the header value.
 *
 * Always emits `HTML_TAG`, including for a render that fetched nothing. A page
 * with no GraphQL behind it is still a cached HTML object, and a deploy purge
 * that skipped it would leave it serving the previous build's markup until its
 * TTL expired.
 *
 * Over budget it drops every entity tag and emits `spill`, matching the API side.
 * Never a truncated set: CloudFront's own truncation picks arbitrarily, so a partial
 * set makes `#p:123` report success having dropped the page that named product 123.
 * `html`, `nav:` and the announcement bar's tag are outside that budget and survive
 * a spill, which is what makes the deploy purge — and a purge of the chrome every
 * page renders — independent of how many entities a page happens to name.
 */
export function formatHtmlCacheTags(tags: Iterable<string>): string {
  // Drop any inbound `html`: it is emitted unconditionally below, and leaving a
  // duplicate in would spend an entity slot on it.
  const all = [...new Set(tags)].filter(t => t !== HTML_TAG)
  const nav = all.filter(t => t.startsWith('nav:'))
  const chrome = all.filter(isChromeTag)
  const entities = all.filter(t => !t.startsWith('nav:') && !isChromeTag(t))

  // The budget leaves no spare slot. It relies on a render carrying at most one
  // `nav:` and one chrome tag, which holds for one locale and one bar per render.
  // If that ever breaks, spill early. A 51st tag would be dropped by CloudFront
  // at random, which is the partial set this function exists to prevent.
  const budget = Math.min(HTML_ENTITY_TAG_BUDGET, CLOUDFRONT_TAG_LIMIT - 1 - nav.length - chrome.length)

  if (entities.length > budget)
    return [HTML_TAG, ...nav, ...chrome, SPILL_TAG].join(',')

  return [HTML_TAG, ...nav, ...chrome, ...entities].join(',')
}
